import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  cloneSystemTemplate,
  createSummaryTemplate,
  deleteSummaryTemplate,
  getSummaryTemplate,
  hideSystemTemplate,
  listSummaryTemplates,
  setDefaultTemplate,
  unhideSystemTemplate,
  updateSummaryTemplate,
} from "../api/summaryTemplate";
import type {
  CloneSystemTemplateRequest,
  CreateSummaryTemplateRequest,
  UpdateSummaryTemplateRequest,
} from "../types/summaryTemplate/SummaryTemplate";

const QUERY_KEY = "summary-templates";

export function useSummaryTemplates(customerId: string | undefined) {
  return useQuery({
    queryKey: [QUERY_KEY, customerId],
    queryFn: () => listSummaryTemplates(customerId!),
    enabled: !!customerId,
  });
}

export function useSummaryTemplate(
  customerId: string | undefined,
  templateId: string | undefined,
) {
  return useQuery({
    queryKey: [QUERY_KEY, customerId, templateId],
    queryFn: () => getSummaryTemplate(customerId!, templateId!),
    enabled: !!customerId && !!templateId,
  });
}

export function useCreateSummaryTemplate(customerId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: CreateSummaryTemplateRequest) =>
      createSummaryTemplate(customerId!, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, customerId] });
    },
  });
}

export function useUpdateSummaryTemplate(customerId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ templateId, request }: { templateId: string; request: UpdateSummaryTemplateRequest }) =>
      updateSummaryTemplate(customerId!, templateId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, customerId] });
    },
  });
}

export function useDeleteSummaryTemplate(customerId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (templateId: string) => deleteSummaryTemplate(customerId!, templateId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, customerId] });
    },
  });
}

export function useSetDefaultTemplate(customerId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (templateId: string) => setDefaultTemplate(customerId!, templateId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, customerId] });
    },
  });
}

export function useHideSystemTemplate(customerId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (systemTemplateId: string) => hideSystemTemplate(customerId!, systemTemplateId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, customerId] });
    },
  });
}

export function useUnhideSystemTemplate(customerId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (systemTemplateId: string) => unhideSystemTemplate(customerId!, systemTemplateId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, customerId] });
    },
  });
}

export function useCloneSystemTemplate(customerId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ systemTemplateId, request }: { systemTemplateId: string; request: CloneSystemTemplateRequest }) =>
      cloneSystemTemplate(customerId!, systemTemplateId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY, customerId] });
    },
  });
}
